import { useMemo } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { AvailableModules } from 'generated/featureDefinitions'
import {
  GetModuleAvailabilitiesDocument,
  TrainingDetailPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useQuery } from 'modules/core/hooks/useQuery'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

const DetailRevamp = () => {
  const { id } = useParams()
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data, loading } = useQuery(TrainingDetailPageDocument, {
    variables: {
      trainingId: String(id),
      companyId,
    },
    fetchPolicy: 'cache-first',
  })

  const training = data?.trainings.training

  const canEdit = Boolean(training?._policies?.edit.can)
  const {
    has_dev_trainings: hasDevTrainings = false,
    has_dev_training_bulk_import_certificates: hasBulkImportCertificates = false,
    has_dev_trainings_scorm_cloud: hasScormFF = false,
  } = checkFeatures(
    ['dev_trainings', 'dev_training_bulk_import_certificates', 'dev_trainings_scorm_cloud'],
    data?.features?.featuresConnection.nodes
  )

  const { data: featureData } = useQuery(GetModuleAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      ids: [AvailableModules.lms],
      companyId,
    },
  })

  const moduleAvailabilities = featureData?.companies.moduleAvailabilities ?? []
  const hasLMSModule = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.lms)

  const hasContentFF = hasLMSModule

  const isScormTraining = Boolean(training?.scormPackage)

  const outletContext = useMemo(
    () => ({
      classes: training?.trainingClassesConnection?.nodes ?? [],
      completionConfiguration: training?.completionConfiguration ?? [],
      training,
      competencies: training?.competenciesConnection.nodes ?? [],
      members: data?.trainings.trainingMembershipsConnection?.nodes ?? [],
      canEdit,
      hasDevTrainings,
      hasContentFF,
      hasBulkImportCertificates,
      isScormTraining,
      hasScormFF,
      participantEmployeeIds: [],
    }),
    [
      training,
      data?.trainings.trainingMembershipsConnection?.nodes,
      canEdit,
      hasDevTrainings,
      hasContentFF,
      hasBulkImportCertificates,
      isScormTraining,
      hasScormFF,
    ]
  )

  if (loading || !data) {
    return null
  }

  return <Outlet context={outletContext} />
}

export default DetailRevamp
